import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingState } from "@/components/common/States";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, AlertTriangle, RotateCcw, CheckCircle2 } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// Shared "safe delete with inventory reversal" flow for a purchase bill.
// Fetches delete-impact, shows reversal / blockers / dependent transactions, then deletes.
export default function DeletePurchaseBillDialog({ billId, invoiceNumber, supplierName, onDeleted, trigger }) {
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState("");

  const load = async () => {
    setOpen(true); setLoading(true); setImpact(null);
    try {
      const { data } = await api.get(`/purchase-bills/${billId}/delete-impact`);
      setImpact(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not load delete impact")); setOpen(false); }
    finally { setLoading(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/purchase-bills/${billId}?reverseStock=true&reason=${encodeURIComponent(reason)}`);
      const rev = res.data?.stockReversed?.length;
      toast.success(rev ? `Invoice deleted · stock reversed for ${rev} item(s)` : "Invoice deleted");
      setOpen(false); setReason("");
      onDeleted?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setDeleting(false); }
  };

  return (
    <>
      <span onClick={load} data-testid={`pbdel-trigger-${billId}`}>{trigger}</span>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setReason(""); }}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="pbdel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><Trash2 className="h-4 w-4" /> Delete Purchase Invoice?</DialogTitle>
            <DialogDescription>
              {invoiceNumber ? <b className="font-mono">{invoiceNumber}</b> : "This invoice"}{supplierName ? ` · ${supplierName}` : ""}. A deletion audit record is always kept.
            </DialogDescription>
          </DialogHeader>

          {loading ? <LoadingState label="Checking inventory & links…" /> : impact && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                <span className="text-stone-600">Invoice value</span><b>{money(impact.amount)}</b>
              </div>

              {impact.blockers?.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700" data-testid="pbdel-blockers">
                  <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Cannot delete</div>
                  <div className="mt-1 text-xs">This invoice has {impact.blockers.join(", ")}. Reverse those first.</div>
                </div>
              )}

              {impact.stockReceived && impact.stockReversal?.length > 0 && (
                <div className="rounded-lg border border-stone-200 p-3" data-testid="pbdel-reversal">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase text-stone-500">
                    <RotateCcw className="h-3.5 w-3.5" /> Stock that will be reversed
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-left text-[10px] uppercase text-stone-400"><tr><th className="py-1">Ingredient</th><th className="py-1 text-right">Reverse Qty</th><th className="py-1 text-right">On Hand</th><th className="py-1 text-center">OK?</th></tr></thead>
                    <tbody className="divide-y divide-stone-100">
                      {impact.stockReversal.map((r) => (
                        <tr key={r.ingredientId} data-testid={`pbdel-rev-${r.ingredientId}`}>
                          <td className="py-1">{r.name}</td>
                          <td className="py-1 text-right tabular-nums">{num(r.qtyToReverse)} {r.unit}</td>
                          <td className="py-1 text-right tabular-nums">{num(r.currentStock)} {r.unit}</td>
                          <td className="py-1 text-center">
                            {r.canReverse ? <CheckCircle2 className="mx-auto h-3.5 w-3.5 text-emerald-600" />
                              : <span className="text-rose-600">short {num(r.shortfall)}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {impact.consumedShort && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800" data-testid="pbdel-consumed">
                  <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Stock already consumed</div>
                  <div className="mt-1 text-xs">Some received quantity is no longer in stock (used in production/transfers). Reverse those first — deletion is blocked.</div>
                </div>
              )}

              {impact.dependentTransactions?.length > 0 && (
                <div className="max-h-28 overflow-auto rounded-lg border border-stone-200 p-2" data-testid="pbdel-dependents">
                  <div className="mb-1 text-[10px] font-semibold uppercase text-stone-400">Dependent transactions after receipt</div>
                  {impact.dependentTransactions.map((d, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-stone-600">
                      <span>{d.date} · {d.type} · {d.ingredient}</span><span className="tabular-nums">−{num(d.qty)}</span>
                    </div>
                  ))}
                </div>
              )}

              {impact.canDelete && impact.willReverseStock && (
                <Badge className="border-0 bg-amber-100 text-[11px] text-amber-800">On delete, the received stock above will be reversed out of inventory.</Badge>
              )}

              <Input data-testid="pbdel-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 text-sm" />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="pbdel-cancel">Cancel</Button>
            <Button className="gap-1.5 bg-rose-600 hover:bg-rose-700" data-testid="pbdel-confirm"
              disabled={deleting || loading || !impact?.canDelete} onClick={doDelete}>
              <Trash2 className="h-4 w-4" /> {impact?.willReverseStock ? "Reverse Stock & Delete" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
